// ./src/components/features/Refuels/FuelTypeSelector/FuelTypeSelector.tsx
import React, { useMemo, useEffect } from "react";
import { Select, Group, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon } from "@/components/generic";
import {
  FUEL_TYPE_OPTIONS,
  getFuelTypeIcon,
  FUEL_TYPE_MAP,
} from "@/config/fuelTypes";

import { DRAWER_COMBOBOX_PROPS } from "@/utils/mantineUiHelpers";

// ============================================
// Types
// ============================================

export interface FuelTypeSelectorProps {
  /** Field name for form binding */
  name?: string;

  /** Currently selected fuel type value */
  value?: string | null | undefined;

  /** Callback fired when fuel type changes: (name, value) */
  onChange?: (name: string, value: string | null) => void;

  /** Placeholder text */
  placeholder?: string;

  /** Whether the field is required (auto-selects first option if null) */
  required?: boolean;

  /** Error message to display */
  error?: string | null | undefined;

  /** Whether the selector is disabled */
  disabled?: boolean;

  /** Size of the selector */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Label for the field */
  label?: string;
}

// ============================================
// Component
// ============================================

const FuelTypeSelectorComponent: React.FC<FuelTypeSelectorProps> = ({
  name = "fuelType",
  value,
  onChange,
  placeholder,
  required = false,
  error,
  disabled = false,
  size = "md",
  label,
}) => {
  const { t } = useTranslation(["refuels", "common"]);

  // Build options list with translated labels
  const options = useMemo(
    () =>
      FUEL_TYPE_OPTIONS.map((preset) => ({
        value: preset.value,
        label: t(`common:${preset.labelKey}`),
      })),
    [t]
  );

  // Auto-select first option if required and no value
  useEffect(() => {
    if (required && !value && options.length > 0) {
      onChange?.(name, options[0].value);
    }
  }, [required, value, options, name, onChange]);

  // Get icon for current value
  const currentIcon = useMemo(() => getFuelTypeIcon(value), [value]);

  // Handle selection change
  const handleChange = (selectedValue: string | null) => {
    onChange?.(name, selectedValue);
  };

  // Render option with icon
  const renderOption: React.ComponentProps<typeof Select>["renderOption"] = ({
    option,
  }) => {
    const fuelType = FUEL_TYPE_MAP[option.value];
    const iconName = fuelType?.iconName ?? "GasStation";

    return (
      <Group gap="sm" wrap="nowrap">
        <AppIcon iconName={iconName} size={20} />
        <Text size="md">{option.label}</Text>
      </Group>
    );
  };

  // Default translations
  const defaultPlaceholder =
    placeholder || t("refuels:form.fields.fuelType.placeholder");
  const defaultLabel = label ?? t("refuels:form.fields.fuelType.label");

  return (
    <Select
      label={defaultLabel}
      placeholder={defaultPlaceholder}
      data={options}
      value={value || null}
      onChange={handleChange}
      disabled={disabled}
      required={required}
      clearable={!required}
      searchable={false}
      error={error}
      size={size}
      leftSection={<AppIcon iconName={currentIcon} size={18} />}
      renderOption={renderOption}
      comboboxProps={DRAWER_COMBOBOX_PROPS}
    />
  );
};

export const FuelTypeSelector = React.memo(FuelTypeSelectorComponent);